import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import { withRouter } from 'next/router';
import { slugify, kbygPaths } from '@surfline/web-common';

import { scrollToSpot } from 'actions/animations';
import { setMapLocation } from 'actions/map';
import GeonameMapPageMeta from 'components/GeonameMapPageMeta';
import Spinner from 'components/Spinner';
import SpotMap from 'containers/SpotMap';
import SpotList from 'containers/SpotList';
import locationViewPropShape from 'propTypes/locationView';
import spotPropShape from 'propTypes/spot';
import { SYNC_ONLY_ON_MAP_INTERACTIONS } from 'propTypes/map/mapUrlSync';
import mapOptions from 'utils/mapOptions';
import { camsAndForecastsPaths } from 'utils/urls/camsAndForecastsPath';
import { getMapPageWidth } from 'utils/dimensions';

import styles from './GeonameMap.module.scss';

const { spotReportPath } = kbygPaths;

const getRightRailClass = () => classNames(styles.slRightRail, 'sl-right-rail');
const getLeftRailClass = () => classNames(styles.slLeftRail, 'sl-left-rail');

class GeonameMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMapLoading: true,
      mapInitialized: false,
    };
  }

  componentDidMount() {
    const { locationView } = this.props;
    const { mapInitialized } = this.state;
    // Only set map location after map is initialized
    if (mapInitialized) {
      this.fitBoundsAndSetMapLocation(locationView.boundingBox);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { locationView, spots } = this.props;
    const { mapInitialized } = this.state;

    // Set map location on bounding box change or when map becomes initialized
    if (
      (locationView.boundingBox !== prevProps.locationView.boundingBox ||
        (!prevState.mapInitialized && mapInitialized)) &&
      mapInitialized
    ) {
      this.fitBoundsAndSetMapLocation(locationView.boundingBox);
    }

    // If spots appear and we were previously showing loading, ensure we hide loading
    if (prevProps.spots.length === 0 && spots.length > 0) {
      this.setState({ isMapLoading: false });
    }
  }

  fitBoundsAndSetMapLocation = (boundingBox) => {
    const { doSetMapLocation } = this.props;

    if (boundingBox) {
      const height = this.mapRef.clientHeight ? this.mapRef.clientHeight : 1000;
      const width = this.mapRef.clientWidth ? this.mapRef.clientWidth : 1000;
      const { north: lat0, west: lon0, south: lat1, east: lon1 } = boundingBox;
      const viewport = new WebMercatorViewport({ width, height }).fitBounds([
        [lon0, lat0],
        [lon1, lat1],
      ]);
      doSetMapLocation({
        center: { lat: viewport.latitude, lon: viewport.longitude },
        zoom: viewport.zoom,
      });
    }
  };

  goToSpot = (spot) => {
    const { router } = this.props;
    return router.push(spotReportPath(slugify(spot.name), spot._id));
  };

  handleMapLoadingChange = (isLoading) => {
    this.setState((prevState) => ({
      isMapLoading: isLoading,
      // When loading completes, mark map as initialized
      mapInitialized: prevState.mapInitialized || !isLoading,
    }));
  };

  render() {
    const { locationView, spots, loadingSpots, doScrollToSpot } = this.props;
    const { isMapLoading } = this.state;
    const urlPath =
      locationView && locationView.url && `${camsAndForecastsPaths.base}${locationView.url}`;

    // Show loading state when spots are being loaded or map isn't ready
    // but don't keep showing loading if we have spots and only the map is slow
    const isLoading = loadingSpots || (isMapLoading && spots.length === 0) || locationView.loading;

    return (
      <div className={styles.slMapPage} data-testid="sl-geoname-map-page">
        <GeonameMapPageMeta name={locationView.taxonomy.name || ''} urlPath={urlPath} />
        <div
          className={getRightRailClass()}
          ref={(ref) => {
            this.mapRef = ref;
          }}
        >
          <SpotMap
            mapOptions={mapOptions}
            showBottomControls
            clusterMaxZoom={11}
            syncUrl={SYNC_ONLY_ON_MAP_INTERACTIONS}
            storeLocationInSession
            onClickSpotMarker={getMapPageWidth() < 992 ? doScrollToSpot : this.goToSpot}
            onLoadingChange={this.handleMapLoadingChange}
          />
        </div>
        <div className={getLeftRailClass()}>
          {isLoading && spots.length === 0 ? (
            <div className={styles.spinner}>
              <Spinner />
            </div>
          ) : (
            <div>
              <SpotList locationView={locationView} />
            </div>
          )}
        </div>
      </div>
    );
  }
}

GeonameMap.propTypes = {
  doScrollToSpot: PropTypes.func.isRequired,
  doSetMapLocation: PropTypes.func.isRequired,
  locationView: locationViewPropShape,
  spots: PropTypes.arrayOf(spotPropShape),
  loadingSpots: PropTypes.bool,
};

GeonameMap.defaultProps = {
  locationView: {
    boundingBox: {},
    taxonomy: {
      name: null,
      location: {
        coordinates: [],
      },
    },
  },
  spots: [],
  loadingSpots: false,
};

export default withRouter(
  connect(
    (state) => ({
      loadingSpots: state.status.loading,
      locationView: state.map.locationView,
      spots: state.map.spots,
    }),
    (dispatch, props) => ({
      doSetMapLocation: (location) => dispatch(setMapLocation(location, props.router, false, true)),
      doScrollToSpot: (spot) => dispatch(scrollToSpot(spot._id)),
    }),
  )(GeonameMap),
);
